import React, { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import throttle from 'lodash/throttle';

import { isEmptyString, isConsumerDomain } from 'Shared/helpers';
import GoogleButton from 'Shared/components/GoogleButton/GoogleButton';
import Office365Button from 'Shared/components/Office365Button/Office365Button';
import TextField from 'Shared/components/TextField/TextField';
import CsrfInput from 'Dashboard/components/CsrfInput/CsrfInput';
import PasswordField from 'Dashboard/components/PasswordField/PasswordField';
import WpButton from 'Wpds/components/button/WpButton';
import SignUpTermsAgreement from 'Shared/components/SignUpTermsAgreement/SignUpTermsAgreement';

import './ZoomCreateAccount.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

type Props = {
  profile: {
    email: string;
  };
};

function ZoomCreateAccount({ profile }: Props) {
  const {
    isZoom,
    app: {
      routes: { googleSignUpUrl, office365SignUpUrl, signUpUrl, signInUrl },
    },
  } = useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({ 'user[email]': profile?.email || '', 'user[password]': '' });
  const [emailErrorMessage, setEmailErrorMessage] = useState(null);
  const formRef = useRef(null);

  const checkWorkEmail = useCallback(email => {
    if (isConsumerDomain(email)) setEmailErrorMessage('Please use your work email');
    else setEmailErrorMessage(null);
  }, []);

  const throttleCheckWorkEmail = useCallback(
    throttle(email => checkWorkEmail(email), 500, { leading: false, trailing: true }),
    [checkWorkEmail]
  );

  const handleTextFieldChange = event => {
    if (event.target.name === 'user[email]') throttleCheckWorkEmail(event.target.value);
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleSubmitClick = () => {
    setIsSubmitting(true);
    formRef.current.submit();
  };

  const isSubmitDisabled =
    isSubmitting ||
    isEmptyString(formValues['user[email]']) ||
    isEmptyString(formValues['user[password]']) ||
    emailErrorMessage;

  const onboardingStateUuid = useSelector(state => state.onboardingState.uuid);
  const additionalQueryParams = `&onboarding_state_token=${onboardingStateUuid}`;
  const additionalQueryParamsForSignUpURL = `?onboarding_state_token=${onboardingStateUuid}`;

  const handleSignInClick = event => {
    if (isZoom) {
      event.preventDefault();
      (window as any).zoomSdk.openUrl({ url: `${window.location.origin}${signInUrl}` });
    }
  };

  return (
    <div className="ZoomCreateAccount px-sm-5 pt-2 d-flex flex-column align-items-center">
      <div className="ZoomCreateAccount__title my-4 text-center px-sm-3">Create Your PI Perform account</div>
      <div className="px-sm-3 w-100">
        <GoogleButton
          openNewTab={false}
          openWithZoomSDK={isZoom}
          className="mt-4"
          googleSignUpUrl={`${googleSignUpUrl}${additionalQueryParams}`}
        >
          Sign up with Google
        </GoogleButton>
        <Office365Button
          openNewTab={false}
          openWithZoomSDK={isZoom}
          className="mt-3"
          office365SignUpUrl={`${office365SignUpUrl}${additionalQueryParams}`}
        >
          Sign up with Office 365
        </Office365Button>
      </div>
      <div className="my-3">or</div>
      <div className="w-100 px-sm-3">
        <form action={`${signUpUrl}${additionalQueryParamsForSignUpURL}`} ref={formRef} method="POST">
          <CsrfInput />
          <TextField
            name="user[email]"
            type="text"
            label="Sign Up With Your Work Email"
            labelMuted={false}
            placeholder="Enter your work email"
            errorMessage={emailErrorMessage}
            value={formValues['user[email]']}
            onChange={handleTextFieldChange}
          />
          <PasswordField
            name="user[password]"
            label="Create Password"
            placeholder="Create Password"
            value={formValues['user[password]']}
            onChange={handleTextFieldChange}
          />
          <WpButton
            size="lg"
            color="primary"
            onClick={handleSubmitClick}
            block
            disabled={isSubmitDisabled}
            loading={isSubmitting}
          >
            Confirm
          </WpButton>
        </form>
      </div>
      <SignUpTermsAgreement className="ZoomCreateAccount__terms text-center mt-4" />
      <div className="ZoomCreateAccount__terms mt-3">
        Already have an account?{' '}
        <a href={signInUrl} onClick={handleSignInClick}>
          Sign in
        </a>
      </div>
    </div>
  );
}

export default ZoomCreateAccount;
